'use client';

import {
  Button,
  ColorInputField,
  ColorPicker,
  Slider,
  Switch,
  ValueInputField,
} from '@crabs-ai/ds-react';
import { Replay } from '@crabs-ai/web-icons';
import { useMemo, type ReactNode } from 'react';

import {
  AUTHORING_WRITING_GUIDE_VISUAL_DEFAULTS,
  AUTHORING_WRITING_GUIDE_VISUAL_SCALE_MIN,
  AUTHORING_WRITING_GUIDE_VISUAL_SCALE_SLIDER_MAX,
  normalizeAuthoringWritingGuideHexColor,
  normalizeAuthoringWritingGuideVisualScale,
  type AuthoringWritingGuideStyle,
} from '@/shared/lib/writing-guide/authoring-style';

import {
  isAuthoringWritingGuideCodeItem,
  type CodeItem,
} from '../items/code/code-item-type';
import { readEmbeddedAuthoringStyle } from '../items/code/authoring-embedded-runtime';
import { changeItems } from '../state/actions/change-items';
import { useWriteContext } from '../utils/use-context';

interface WritingGuideInspectorProps {
  item: CodeItem;
}

const WRITING_GUIDE_COLOR_DEFAULTS = {
  backgroundFillColor: '#FFFFFF',
  baseFillColor: '#FFFFFF',
  fillColor: '#168A45',
  outlineColor: '#111111',
};

const EDITABLE_STYLE_KEYS = [
  'backgroundFillColor',
  'baseFillColor',
  'fillColor',
  'outlineColor',
  'guideColor',
  'guideStrokeScale',
  'pencilVisualScale',
  'showOriginalGuideOverlay',
] as const satisfies readonly (keyof AuthoringWritingGuideStyle)[];

export function WritingGuideInspector({ item }: WritingGuideInspectorProps) {
  const { setState } = useWriteContext();
  const effectiveStyle = useMemo(() => readEffectiveStyle(item), [item]);
  const hasOverrides = EDITABLE_STYLE_KEYS.some(
    (key) => item.authoringStyle?.[key] !== undefined,
  );

  const updateAuthoringStyle = (
    patch: AuthoringWritingGuideStyle,
    commitToUndoStack: boolean,
  ) => {
    setState({
      update: (state) =>
        changeItems(state, [item.id], (current) => {
          if (
            current.type !== 'code' ||
            !isAuthoringWritingGuideCodeItem(current)
          ) {
            return current;
          }
          return {
            ...current,
            authoringStyle: {
              ...(current.authoringStyle ?? {}),
              ...patch,
            },
          };
        }),
      commitToUndoStack,
    });
  };

  const resetAuthoringStyle = () => {
    setState({
      update: (state) =>
        changeItems(state, [item.id], (current) => {
          if (
            current.type !== 'code' ||
            !isAuthoringWritingGuideCodeItem(current)
          ) {
            return current;
          }
          const nextStyle = {
            ...((current.authoringStyle ?? {}) as Record<string, unknown>),
          };
          for (const key of EDITABLE_STYLE_KEYS) delete nextStyle[key];
          return {
            ...current,
            authoringStyle:
              Object.keys(nextStyle).length > 0
                ? (nextStyle as AuthoringWritingGuideStyle)
                : undefined,
          };
        }),
      commitToUndoStack: true,
    });
  };

  return (
    <div className="flex flex-col gap-md p-sm">
      <InspectorSection title="글자 색상">
        <WritingGuideColorControl
          label="배경"
          value={effectiveStyle.backgroundFillColor}
          onChange={(value, commitToUndoStack) =>
            updateAuthoringStyle(
              { backgroundFillColor: value },
              commitToUndoStack,
            )
          }
        />
        <WritingGuideColorControl
          label="채움"
          value={effectiveStyle.fillColor}
          onChange={(value, commitToUndoStack) =>
            updateAuthoringStyle({ fillColor: value }, commitToUndoStack)
          }
        />
        <WritingGuideColorControl
          label="기본 글자"
          value={effectiveStyle.baseFillColor}
          onChange={(value, commitToUndoStack) =>
            updateAuthoringStyle({ baseFillColor: value }, commitToUndoStack)
          }
        />
        <WritingGuideColorControl
          label="테두리"
          value={effectiveStyle.outlineColor}
          onChange={(value, commitToUndoStack) =>
            updateAuthoringStyle({ outlineColor: value }, commitToUndoStack)
          }
        />
      </InspectorSection>

      <InspectorSection title="원본 가이드">
        <div className="flex items-center justify-between gap-sm">
          <span className="text-comp-moderate-medium text-sys-cont-neutral-default">
            표시
          </span>
          <Switch
            checked={effectiveStyle.showOriginalGuideOverlay}
            onCheckedChange={(checked) =>
              updateAuthoringStyle({ showOriginalGuideOverlay: checked }, true)
            }
          />
        </div>
        <WritingGuideColorControl
          label="색상"
          value={effectiveStyle.guideColor}
          disabled={!effectiveStyle.showOriginalGuideOverlay}
          onChange={(value, commitToUndoStack) =>
            updateAuthoringStyle({ guideColor: value }, commitToUndoStack)
          }
        />
        <WritingGuideScaleControl
          label="두께"
          value={effectiveStyle.guideStrokeScale}
          disabled={!effectiveStyle.showOriginalGuideOverlay}
          onChange={(value, commitToUndoStack) =>
            updateAuthoringStyle({ guideStrokeScale: value }, commitToUndoStack)
          }
        />
      </InspectorSection>

      <InspectorSection title="연필">
        <WritingGuideScaleControl
          label="크기"
          value={effectiveStyle.pencilVisualScale}
          onChange={(value, commitToUndoStack) =>
            updateAuthoringStyle(
              { pencilVisualScale: value },
              commitToUndoStack,
            )
          }
        />
      </InspectorSection>

      <div className="flex justify-end">
        <Button
          type="button"
          size="small"
          hierarchy="tertiary"
          leadingNode={<Replay className="size-4" />}
          disabled={!hasOverrides}
          state={hasOverrides ? 'default' : 'disabled'}
          onClick={resetAuthoringStyle}
        >
          기본값으로 되돌리기
        </Button>
      </div>
    </div>
  );
}

function InspectorSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="flex flex-col gap-sm border-b border-sys-style-neutral-light pb-md last:border-b-0 last:pb-0">
      <h3 className="text-comp-accent-small text-sys-cont-neutral-default">
        {title}
      </h3>
      {children}
    </section>
  );
}

function WritingGuideColorControl({
  label,
  value,
  disabled = false,
  onChange,
}: {
  label: string;
  value: string;
  disabled?: boolean;
  onChange: (value: string, commitToUndoStack: boolean) => void;
}) {
  return (
    <label className="flex min-w-0 items-center justify-between gap-sm">
      <span className="text-comp-moderate-medium text-sys-cont-neutral-default">
        {label}
      </span>
      <div className="w-36 min-w-0">
        <ColorInputField
          accessibilityLabel={`${label} 색상`}
          disabled={disabled}
          type="default"
          value={value}
          dropdownContent={
            <ColorPicker
              value={value}
              onChange={(nextValue) => onChange(nextValue ?? value, false)}
              onChangeEnd={(nextValue) => onChange(nextValue ?? value, true)}
            >
              <ColorPicker.ModeSwitch />
              <ColorPicker.Area />
              <ColorPicker.Input />
              <ColorPicker.Presets />
            </ColorPicker>
          }
        />
      </div>
    </label>
  );
}

function WritingGuideScaleControl({
  label,
  value,
  disabled = false,
  onChange,
}: {
  label: string;
  value: number;
  disabled?: boolean;
  onChange: (value: number, commitToUndoStack: boolean) => void;
}) {
  const percent = Math.round(value * 100);
  const updatePercent = (nextPercent: number, commitToUndoStack: boolean) => {
    onChange(
      normalizeAuthoringWritingGuideVisualScale(nextPercent / 100, value),
      commitToUndoStack,
    );
  };

  return (
    <label className="flex min-w-0 flex-col gap-2xs">
      <span className="text-comp-moderate-medium text-sys-cont-neutral-default">
        {label}
      </span>
      <div className="flex min-w-0 items-center gap-sm">
        <Slider
          aria-label={label}
          className="min-w-0 flex-1"
          disabled={disabled}
          isShowTooltip={false}
          min={AUTHORING_WRITING_GUIDE_VISUAL_SCALE_MIN * 100}
          max={AUTHORING_WRITING_GUIDE_VISUAL_SCALE_SLIDER_MAX * 100}
          step={5}
          value={[
            Math.min(
              percent,
              AUTHORING_WRITING_GUIDE_VISUAL_SCALE_SLIDER_MAX * 100,
            ),
          ]}
          onValueChange={(values) => updatePercent(values[0] ?? percent, false)}
          onValueCommit={(values) => updatePercent(values[0] ?? percent, true)}
        />
        <ValueInputField
          accessibilityLabel={`${label} 퍼센트`}
          className="w-24 shrink-0"
          disabled={disabled}
          isUnit="%"
          min={AUTHORING_WRITING_GUIDE_VISUAL_SCALE_MIN * 100}
          max={null}
          step={5}
          value={percent}
          setValue={(update) =>
            updatePercent(update.num, update.commitToUndoStack)
          }
        />
      </div>
    </label>
  );
}

function readEffectiveStyle(item: CodeItem) {
  const specStyle =
    item.authoringSpec?.style ?? readEmbeddedAuthoringStyle(item.code) ?? {};
  const merged = {
    ...specStyle,
    ...(item.authoringStyle ?? {}),
  };
  const visual = {
    guideColor: normalizeAuthoringWritingGuideHexColor(
      merged.guideColor,
      AUTHORING_WRITING_GUIDE_VISUAL_DEFAULTS.guideColor,
    ),
    guideStrokeScale: normalizeAuthoringWritingGuideVisualScale(
      merged.guideStrokeScale,
      AUTHORING_WRITING_GUIDE_VISUAL_DEFAULTS.guideStrokeScale,
    ),
    pencilVisualScale: normalizeAuthoringWritingGuideVisualScale(
      merged.pencilVisualScale,
      AUTHORING_WRITING_GUIDE_VISUAL_DEFAULTS.pencilVisualScale,
    ),
    showOriginalGuideOverlay:
      typeof merged.showOriginalGuideOverlay === 'boolean'
        ? merged.showOriginalGuideOverlay
        : AUTHORING_WRITING_GUIDE_VISUAL_DEFAULTS.showOriginalGuideOverlay,
  };

  return {
    backgroundFillColor: normalizeAuthoringWritingGuideHexColor(
      merged.backgroundFillColor,
      WRITING_GUIDE_COLOR_DEFAULTS.backgroundFillColor,
    ),
    baseFillColor: normalizeAuthoringWritingGuideHexColor(
      merged.baseFillColor,
      WRITING_GUIDE_COLOR_DEFAULTS.baseFillColor,
    ),
    fillColor: normalizeAuthoringWritingGuideHexColor(
      merged.fillColor,
      WRITING_GUIDE_COLOR_DEFAULTS.fillColor,
    ),
    outlineColor: normalizeAuthoringWritingGuideHexColor(
      merged.outlineColor,
      WRITING_GUIDE_COLOR_DEFAULTS.outlineColor,
    ),
    ...visual,
  };
}

export default WritingGuideInspector;
